import { signatureService } from "./signatureService";
import { ComposeMode } from "../types/compose";

// ─── Public types ─────────────────────────────────────────────────────────────

/**
 * Returned by insertSignatureIntoComposeBody() so the task pane can show
 * the right feedback message without catching a non-error condition as an
 * exception.
 *
 *  "inserted"       — signature was written to the message body successfully.
 *  "already-present" — a SignaFlow signature was already detected; nothing changed.
 */
export type InsertSignatureResult = "inserted" | "already-present" | "not-allowed";

// ─── Internal helpers ─────────────────────────────────────────────────────────

/** Returns the active compose item, or throws if the Office context is unavailable. */
function getComposeItem(): Office.MessageCompose {
  const item = Office.context.mailbox?.item;
  if (!item) {
    throw new Error("Outlook item context is not available.");
  }
  return item as Office.MessageCompose;
}

/** True when the item exposes a writable body (i.e. we are in compose mode). */
function isComposeItem(item: Office.MessageCompose): boolean {
  return !!item.body;
}

// ─── detectComposeMode ────────────────────────────────────────────────────────

/**
 * Resolves the current compose type ("new" | "reply" | "forward" | "unknown").
 * Uses getComposeTypeAsync which is available from Mailbox 1.1 onwards.
 */
export function detectComposeMode(): Promise<ComposeMode> {
  return new Promise((resolve) => {
    const item = getComposeItem();

    if (!isComposeItem(item)) {
      resolve("unknown");
      return;
    }

    // getComposeTypeAsync is typed separately because older @types/office-js
    // builds may not include it.
    const composeAwareItem = item as Office.MessageCompose & {
      getComposeTypeAsync?: (
        callback?: (result: Office.AsyncResult<{ composeType: string }>) => void
      ) => void;
    };

    if (!composeAwareItem.getComposeTypeAsync) {
      resolve("unknown");
      return;
    }

    composeAwareItem.getComposeTypeAsync((result) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        console.warn("getComposeTypeAsync failed.", result.error);
        resolve("unknown");
        return;
      }

      const composeType = result.value?.composeType?.toLowerCase();

      if (composeType === "new")     { resolve("new");     return; }
      if (composeType === "reply")   { resolve("reply");   return; }
      if (composeType === "forward") { resolve("forward"); return; }

      resolve("unknown");
    });
  });
}

// ─── signatureAlreadyExists ───────────────────────────────────────────────────

/**
 * Reads the current HTML body and returns true when it already contains a
 * SignaFlow signature block (identified by the configured marker attribute).
 *
 * Why a custom data-attribute?
 *   Searching for visible text ("Gaurav Rana") would break if the user edits
 *   the signature or if Outlook re-formats the body.  A dedicated HTML attribute
 *   survives most of Outlook's HTML round-trip normalisation.
 *
 * Used only on the body.setAsync fallback path.  setSignatureAsync (when
 * available) stores signatures in a separate "signature zone" and replaces them
 * on each call — so duplication is impossible on that path.
 */
function signatureAlreadyExists(item: Office.MessageCompose, markerAttribute: string): Promise<boolean> {
  return new Promise((resolve) => {
    item.body.getAsync(Office.CoercionType.Html, (result: Office.AsyncResult<string>) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        // Cannot read the body — assume absent so insertion can proceed.
        console.warn("signatureAlreadyExists: could not read body HTML.", result.error);
        resolve(false);
        return;
      }

      const bodyHtml = result.value ?? "";
      // Compare lower-case to guard against Outlook normalising attribute casing.
      const found = bodyHtml.toLowerCase().includes(markerAttribute.toLowerCase());
      resolve(found);
    });
  });
}

// ─── trySetSignatureAsync (Mailbox 1.10 — preferred path) ────────────────────

/**
 * Calls body.setSignatureAsync() — available in Mailbox 1.10+.
 *
 * setSignatureAsync places the signature in a dedicated "signature zone"
 * below the compose area that is separate from the user's typed content.
 * Calling it multiple times simply REPLACES the previous value — no
 * duplication can occur on this path.
 *
 * Supported clients (as of 2024):
 *   ✓ Classic Outlook for Windows (Microsoft 365 / build 16.0.13430+)
 *   ✓ New Outlook for Windows
 *   ✓ Outlook on the web (Microsoft 365)
 *   ✓ Outlook on Mac (build 16.38+)
 *
 * NOT supported in:
 *   ✗ Perpetual Outlook 2019 / 2021 for Windows
 *   ✗ Older Outlook for Mac perpetual builds
 *
 * Rejects when the API is not present, so the caller can fall back.
 */
function trySetSignatureAsync(
  item: Office.MessageCompose,
  signatureHtml: string
): Promise<void> {
  return new Promise((resolve, reject) => {
    // Cast to include setSignatureAsync, which may be absent from some TS type
    // definition versions even though it exists at runtime.
    const bodyExt = item.body as Office.Body & {
      setSignatureAsync?: (
        data: string,
        options: Office.AsyncContextOptions & { coercionType: Office.CoercionType },
        callback?: (result: Office.AsyncResult<void>) => void
      ) => void;
    };

    if (!bodyExt.setSignatureAsync) {
      reject(new Error("setSignatureAsync is not available in this Outlook client."));
      return;
    }

    bodyExt.setSignatureAsync(
      signatureHtml,
      { coercionType: Office.CoercionType.Html },
      (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve();
        } else {
          reject(result.error);
        }
      }
    );
  });
}

// ─── appendSignatureToBody (Mailbox 1.1 — universal fallback) ─────────────────

/**
 * Fallback insertion when setSignatureAsync is unavailable.
 *
 * Steps:
 *   1. Read the full body as HTML (body.getAsync) to capture existing content.
 *   2. Append two line-breaks + the signature HTML.
 *   3. Write the combined HTML back (body.setAsync).
 *
 * body.setAsync replaces the entire body, which is why we must read first —
 * the user's existing content is always preserved this way.
 *
 * Requires Mailbox 1.1 (universally supported in all modern Outlook clients).
 */
function appendSignatureToBody(
  item: Office.MessageCompose,
  signatureHtml: string
): Promise<void> {
  return new Promise((resolve, reject) => {
    // Step 1 — read the existing body.
    item.body.getAsync(Office.CoercionType.Html, (getResult: Office.AsyncResult<string>) => {
      if (getResult.status !== Office.AsyncResultStatus.Succeeded) {
        reject(getResult.error);
        return;
      }

      const existingBody = getResult.value ?? "";

      // Step 2 — concatenate: preserve everything the user already typed.
      const updatedBody = `${existingBody}<br><br>${signatureHtml}`;

      // Step 3 — write the combined body back.
      item.body.setAsync(
        updatedBody,
        { coercionType: Office.CoercionType.Html },
        (setResult: Office.AsyncResult<void>) => {
          if (setResult.status === Office.AsyncResultStatus.Succeeded) {
            resolve();
          } else {
            reject(setResult.error);
          }
        }
      );
    });
  });
}

// ─── insertSignatureIntoComposeBody (public API) ──────────────────────────────

/**
 * Main entry point called by both:
 *   - task pane manual button (fallback/testing), and
 *   - event-based compose launch handler (automatic mode).
 *
 * Insertion strategy (two paths):
 *
 *   Path A — setSignatureAsync (Mailbox 1.10, preferred):
 *     Writes to the dedicated signature zone.  Handles duplicates natively by
 *     replacing on every call.  Returns "inserted" on success.
 *
 *   Path B — body.getAsync + body.setAsync (Mailbox 1.1, universal fallback):
 *     Used when Path A is unavailable.  Checks for the marker attribute
 *     attribute first to prevent duplicates, then appends the signature below
 *     whatever content already exists in the body.
 *
 * @returns "inserted"        — signature written successfully.
 * @returns "already-present" — duplicate detected (fallback path only); no change made.
 * @throws                    — any unexpected Office.js or network error.
 */
export async function insertSignatureIntoComposeBody(): Promise<InsertSignatureResult> {
  console.log("[SignaFlow] insertSignatureIntoComposeBody invoked.");

  const currentUserEmailAddress = signatureService.getCurrentUserEmailAddress();
  console.log(`[SignaFlow] Current mailbox user: ${currentUserEmailAddress || "unknown"}`);

  if (!signatureService.isSignatureEnabledForCurrentUser()) {
    console.log("[SignaFlow] Signature insertion skipped because current user is not the POC mailbox.");
    return "not-allowed";
  }

  const item = getComposeItem();

  if (!isComposeItem(item)) {
    throw new Error("Current Outlook item is not in compose mode.");
  }

  const signaturePayload = await signatureService.getSignatureForCurrentUser();
  const signatureHtml = signaturePayload.html;

  // ── Path A: setSignatureAsync ─────────────────────────────────────────────
  try {
    console.log("[SignaFlow] Attempting setSignatureAsync path.");
    await trySetSignatureAsync(item, signatureHtml);
    console.log("[SignaFlow] setSignatureAsync path succeeded.");
    // Success — signature set/replaced in the Outlook signature zone.
    return "inserted";
  } catch (setSignatureError) {
    // API not available in this client — proceed to the manual fallback.
    console.info(
      "[SignaFlow] setSignatureAsync unavailable or failed; switching to body.getAsync/setAsync fallback.",
      setSignatureError
    );
  }

  // ── Path B: manual body manipulation ─────────────────────────────────────
  // Guard against duplicates BEFORE calling setAsync, because setAsync
  // replaces the entire body (we cannot undo a second insertion).
  const alreadyPresent = await signatureAlreadyExists(item, signaturePayload.markerAttribute);
  console.log(`[SignaFlow] Existing signature marker found: ${alreadyPresent}`);

  if (alreadyPresent) {
    return "already-present";
  }

  console.log("[SignaFlow] Appending signature via getAsync/setAsync fallback path.");
  await appendSignatureToBody(item, signatureHtml);
  console.log("[SignaFlow] Fallback append path succeeded.");
  return "inserted";
}
